import {firestore} from "../firebase";
import APIResponse from "../model/APIResponse";
import ContentFilterValues from "../model/ContentFilter";
import {Entity, Resource} from "myfitworld-model";
import {sanitizeInput} from "./sanitizeInput";
import merge from "lodash/merge";
import get from 'lodash/get';
import globalState from "../globalState";
import firebase from 'firebase/app';
import queryBuilder from "./queryBuilder";
import clientSideContentFilter from "./ContentFilter/clientSideContentFilter";

type CollectionReference = firebase.firestore.CollectionReference;

export interface ApiInterface<T> {
  get: (id: string) => Promise<T>;
  getCorpUsers: (id: string) => Promise<T>;
  create: (raw: T, errorText?: string) => Promise<APIResponse>;
  update: (raw: T, errorText?: string) => Promise<APIResponse>;
  list: (organizationId: string, wlOrgId?: string, filter?: ContentFilterValues, sortKey?: string, haveMFWContent?: boolean) => Promise<Array<T>>;
}

type Optional<T> = {
  [P in keyof T]?: T[P];
};

const withAuthorIdTag = (endpoint: string) => {
    const userId = globalState.getRawState().currentUserId;
  if ((
    endpoint === 'exercises' || endpoint === 'workouts' || endpoint === 'programs' || endpoint === 'meals' ||
    endpoint === 'groceries' || endpoint==="nutrition_plans" || 'tutorials' || 'educations'
    ) && userId) {
    return { authorId: userId };
  }

  return {};
}

export function createProviderFactory<T extends Resource>(
  endpoint: string,
  defaults?: Optional<T>
): (raw: T) => Promise<APIResponse> {
  return async (raw) => {
    const { id, ...data } = raw;
    return await new Promise((resolve, reject) => {
      firestore
        .collection(endpoint)
        .add({
          ...sanitizeInput(merge(defaults, data)),
          ...withAuthorIdTag(endpoint)
        })
        .then((documentRef) => {
          resolve({ success: true, documentRef: documentRef });
          documentRef.update({ id: documentRef.id });
        })
        .catch((e) => {
          console.log("error creating object", e);
          reject({
            success: false,
            errorMessage:
              "There was an error saving your data. Check if you are connected to the Internet and try again…",
          });
        });
    });
  };
}

export function updateProviderFactory<T extends Resource>(endpoint: string): (data: T) => Promise<APIResponse> {
  return async (data) => {
    
    return await new Promise((resolve, reject) => {
      try {
        const documentRef = firestore
          .collection(endpoint)
          .doc(data.id);

          documentRef.set({ ...sanitizeInput(data) }, { merge: true })
          .then(() => {
            resolve({ success: true, documentRef });
          })
          .catch((e) => {
            console.log(e);
          })
      } catch (e) {
        reject({
          success: false,
          errorMessage:
            "There was an error saving your data. Check if you are connected to the Internet and try again…",
        });
      }
    });
  };
}


export const filterByOrganization = (endpoint: string, organizationId: string, ref: CollectionReference, wlOrgId?: string) => {
  const orgs = [organizationId, process.env.REACT_APP_MFW_ORGANIZATION_ID].filter((_) => !!_);
  switch (endpoint) {
    case "exercises":
    case "nutrition_plans":
    case "workouts":
    case "programs":
    case "equipment":
    case "fitnessActivities":
    case "muscleGroups":
    case "goals":
    case "motorSkills":
    case "sports":
    case "healthIssues":
    case "expertises":
    case "mealTypes":
    case "diet":
    case "counterProductiveFor":
    case "goodFor":
    case "timePreparation":
    case "meals":
    case "groceries":
    case "micronutrient":
    case "category":
    case "glycemic_index_range":
    case "glycemic_load_range":
    case "durationInWeeks":
    case "durationConfiguration":
      return ref.where("organizationId", "in", orgs);
      
    default:
      return ref.where("organizationId", "==", organizationId);
  }
};

const shouldFilterByAuthor = (endpoint: string) => {
  switch (endpoint) {
    case "exercises":
    case "workouts":
    case "programs":
    case "nutrition_plans":
    case "meals":
    case "groceries":
      return true;
    default:
      return false;
  }
}

const shouldShowPublic = (endpoint: string, ref: CollectionReference) => {
  switch (endpoint) {
    case "exercises":
      return ref.where("isPublic", "==", true).orderBy("publicDate", "desc")
    case "workouts":
      return ref.where("isPublic", "==", true).orderBy("publicDate.seconds", "desc")
    case "programs":
    default:
      return ref.where("isPublic", "==", true)
  }
}

export function listProviderFactory<T extends Entity>(
  endpoint: string
): (organizationId: string, wlOrgId?: string, filter?: ContentFilterValues, sortKey?: string) => Promise<Array<T>> {
  return async (organizationId, wlOrgId,contentFilter, sortKey) => {
    return new Promise((resolve) => {
      let query;
      
      // Return tutorials or educations to all admins, trainers, organizations
      if (endpoint === 'tutorials') {
        query = firestore.collection(endpoint);
      } else if (endpoint === 'educations') {
        query = firestore.collection(endpoint);
      } else {
        query = filterByOrganization(endpoint, organizationId, firestore.collection(endpoint), wlOrgId);
      }
      
      if (contentFilter?.showPublic) {
        query = shouldShowPublic(endpoint, firestore.collection(endpoint));
        if(wlOrgId === process.env.REACT_APP_MFW_ORGANIZATION_ID){
          query = query.where("isWhiteLabel", "==", false);
        }
      }

      if (contentFilter) {
        query = queryBuilder(query, contentFilter);
      }

      if (contentFilter?.showPremium) {
        query = firestore.collection(endpoint).where("premiumContent", "==", true);
      }

      query
        .get()
        .then((querySnapshot: any) => {
          let data: Array<T> = [];
          querySnapshot.forEach((snapshot: any) => {
            let docData =  snapshot.data();
            if (docData.dateMetadata?.dateCreated instanceof firebase.firestore.Timestamp) {
              docData.dateMetadata.dateCreated = docData.dateMetadata.dateCreated.toDate();
            }
            if (docData.dateMetadata?.dateModified instanceof firebase.firestore.Timestamp) {
              docData.dateMetadata.dateModified = docData.dateMetadata.dateModified.toDate();
            }
            data.push({
              id: snapshot.id,
              ...docData,
            } as T);
          });
          if (contentFilter) {
            data = clientSideContentFilter(data, contentFilter, shouldFilterByAuthor(endpoint), wlOrgId, endpoint) as Array<T>;
          }
          if (sortKey&&!contentFilter?.showPublic) {
            data.sort((l, r) => get(l, sortKey)?.localeCompare(get(r, sortKey), undefined, {numeric: true, sensitivity: 'base'}));
          }
          resolve(data);
          
        })
        .catch((error: any) => {
          console.error("error fetching list", error);
          resolve([]);
        });
    });
  };
}

export function getProviderFactory<T extends Resource>(endpoint: string): (id: string) => Promise<T> {
  return async (id) => {
    return new Promise((resolve, reject) => {
      firestore
        .collection(endpoint)
        .doc(id)
        .get()
        .then((doc) => {
          if (doc.exists) {
            resolve({ id: doc.id, ...doc.data() } as T);
          } else {
            reject(`${endpoint}/${id} not found!`);
          }
        })
        .catch((error) => {
          console.error(`error getting item ${endpoint}, ${id}`, error);
          resolve(error);
        });
    });
  };
}

function interfaceFactory<T extends Entity>(endpoint: string): ApiInterface<T> {
  return {
    create: createProviderFactory<T>(endpoint),
    update: updateProviderFactory<T>(endpoint),
    list: listProviderFactory<T>(endpoint),
    get: getProviderFactory<T>(endpoint),
    getCorpUsers: getProviderFactory<T>(endpoint),
  };
}

export default interfaceFactory;
